import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import { cn } from '@/lib/utils';
import type { AppStatus } from '@/lib/app-records';

interface StatusPickerProps {
  statuses: AppStatus[];
  value: string;
  disabled?: boolean;
  onChange: (key: string) => void;
}

/** The status of an object as a pill; a click opens the list of statuses. */
export const StatusPicker = ({ statuses, value, disabled, onChange }: StatusPickerProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = statuses.find((status) => status.key === value) ?? { key: value, label: value, tone: 'neutral' as const };

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block" onClick={(event) => event.stopPropagation()}>
      <button type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-label={`Statut : ${current.label}`}
        onClick={() => setOpen((state) => !state)}
        className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition disabled:cursor-default',
          current.done ? 'border-[var(--domino-good)] bg-[var(--domino-good-bg)] text-[var(--domino-good)]' : 'border-domino-cardBorder bg-domino-white text-domino-ink hover:border-domino-aubergine')}>
        {current.done ? <Check className="h-3 w-3" aria-hidden="true" /> : <ToneDot tone={current.tone} />}
        {current.label}
      </button>
      {open && (
        <ul role="listbox" aria-label="Choisir un statut"
          className="absolute left-0 z-20 mt-1 min-w-[10rem] rounded-xl border border-domino-cardBorder bg-domino-white p-1 shadow-lg">
          {statuses.map((status) => (
            <li key={status.key} role="option" aria-selected={status.key === value}>
              <button type="button" onClick={() => { setOpen(false); if (status.key !== value) onChange(status.key); }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
                {status.done ? <Check className="h-3.5 w-3.5 text-[var(--domino-good)]" aria-hidden="true" /> : <ToneDot tone={status.tone} />}
                <span className="flex-1">{status.label}</span>
                {status.key === value && <span className="text-xs text-domino-inkSoft">actuel</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
